import React, { useState, useRef, useEffect } from 'react';

function ConfidenceBadge({ level }) {
  const colors = {
    high: { bg: 'rgba(16, 185, 129, 0.12)', color: '#10b981', border: 'rgba(16, 185, 129, 0.25)', label: 'Alta' },
    medium: { bg: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', border: 'rgba(245, 158, 11, 0.25)', label: 'Média' },
    low: { bg: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.25)', label: 'Baixa' },
  };
  const c = colors[level] || colors.low;
  return (
    <span
      className="waf-confidence-badge"
      style={{ background: c.bg, color: c.color, borderColor: c.border }}
    >
      {c.label}
    </span>
  );
}

function EvidenceTag({ evidence }) {
  const icons = { header: '📋', cookie: '🍪', body: '📄', via: '🔀', status: '🔢' };
  return (
    <span className="waf-evidence-tag">
      <span className="waf-evidence-icon">{icons[evidence.type] || '🔍'}</span>
      <span className="waf-evidence-text">{evidence.detail}</span>
    </span>
  );
}

export default function WafInfo({ wafDetection }) {
  if (!wafDetection) return null;

  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  const { detected, wafList, genericIndicators, rawSignals, error } = wafDetection;

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [wafDetection, isExpanded]);

  const hasGeneric = genericIndicators && genericIndicators.length > 0;
  const hasServerInfo = rawSignals?.server;
  const hasSignificantHeaders = rawSignals?.significantHeaders && Object.keys(rawSignals.significantHeaders).length > 0;

  // Summary for collapsed state
  const summaryParts = [];
  if (detected && wafList?.length > 0) {
    summaryParts.push(`🛡️ ${wafList.map(w => w.name).join(', ')}`);
  } else if (hasGeneric) {
    summaryParts.push('⚠️ Indicadores genéricos');
  } else if (error) {
    summaryParts.push('Erro na detecção');
  } else {
    summaryParts.push('Não detectado');
  }
  if (rawSignals?.server) summaryParts.push(`Server: ${rawSignals.server}`);

  return (
    <div className={`waf-info-card collapsible-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="waf-info-header collapsible-card-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="waf-info-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <h3 className="waf-info-title">Web Application Firewall</h3>
        <div className={`waf-status-badge ${detected ? 'detected' : hasGeneric ? 'partial' : 'none'}`}>
          {detected ? (
            <><span className="waf-status-dot detected" /> Detectado</>
          ) : hasGeneric ? (
            <><span className="waf-status-dot partial" /> Indicadores</>
          ) : error ? (
            <><span className="waf-status-dot error" /> Erro</>
          ) : (
            <><span className="waf-status-dot none" /> Não Detectado</>
          )}
        </div>
        {!isExpanded && summaryParts.length > 0 && (
          <div className="collapsible-card-summary">
            {summaryParts.map((part, i) => (
              <span key={i} className="collapsible-summary-chip">{part}</span>
            ))}
          </div>
        )}
        <button className="collapsible-card-toggle" aria-label={isExpanded ? 'Recolher' : 'Expandir'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`collapsible-chevron ${isExpanded ? 'rotated' : ''}`}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      </div>

      <div
        className="collapsible-card-content"
        style={{ height: isExpanded ? contentHeight : 0 }}
      >
        <div ref={contentRef} className="collapsible-card-inner">
          <div className="waf-info-body">
        {/* Detected WAFs */}
        {detected && wafList.map((waf, i) => (
          <div key={i} className="waf-detected-item">
            <div className="waf-detected-main">
              <div className="waf-detected-name-row">
                <span className="waf-shield-icon">🛡️</span>
                <span className="waf-detected-name">{waf.name}</span>
                <ConfidenceBadge level={waf.confidence} />
              </div>
              <div className="waf-detected-meta">
                <span className="waf-meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  {waf.vendor}
                </span>
                <span className="waf-meta-item">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                  {waf.type}
                </span>
              </div>
            </div>
            {waf.evidences && waf.evidences.length > 0 && (
              <div className="waf-evidences">
                <span className="waf-evidences-label">Evidências:</span>
                <div className="waf-evidences-list">
                  {waf.evidences.map((ev, j) => (
                    <EvidenceTag key={j} evidence={ev} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* No WAF - show verdict */}
        {!detected && !hasGeneric && !error && (
          <div className="waf-none-detected">
            <div className="waf-none-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
              </svg>
            </div>
            <div className="waf-none-text">
              <strong>Nenhum WAF identificado</strong>
              <p>Não foram detectadas assinaturas de Web Application Firewall nos headers, cookies ou corpo da resposta. O servidor pode não estar protegido por um WAF, ou utiliza configurações não detectáveis.</p>
            </div>
          </div>
        )}

        {/* Generic indicators */}
        {!detected && hasGeneric && (
          <div className="waf-generic-section">
            <div className="waf-generic-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Indicadores genéricos detectados
            </div>
            <div className="waf-generic-list">
              {genericIndicators.map((ind, i) => (
                <div key={i} className="waf-generic-item">
                  <span className="waf-generic-dot" />
                  {ind}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="waf-error">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Erro na detecção: {error === 'timeout' ? 'Timeout na conexão' : error}
          </div>
        )}

        {/* Technical details - raw signals */}
        {(hasServerInfo || hasSignificantHeaders) && (
          <div className="waf-raw-signals">
            <div className="waf-raw-title">Sinais Técnicos</div>
            {rawSignals?.redirectChain?.length > 1 && (
              <div className="waf-raw-row">
                <span className="waf-raw-label">Redirect Chain</span>
                <div className="waf-cookies-list">
                  {rawSignals.redirectChain.map((url, i) => (
                    <code key={i} className="waf-cookie-chip">{url.length > 50 ? url.substring(0, 50) + '...' : url}</code>
                  ))}
                </div>
              </div>
            )}
            {hasServerInfo && (
              <div className="waf-raw-row">
                <span className="waf-raw-label">Server</span>
                <code className="waf-raw-value">{rawSignals.server}</code>
              </div>
            )}
            {rawSignals?.via && (
              <div className="waf-raw-row">
                <span className="waf-raw-label">Via</span>
                <code className="waf-raw-value">{String(rawSignals.via).substring(0, 100)}</code>
              </div>
            )}
            {rawSignals?.xCache && (
              <div className="waf-raw-row">
                <span className="waf-raw-label">X-Cache</span>
                <code className="waf-raw-value">{rawSignals.xCache}</code>
              </div>
            )}
            {rawSignals?.statusCode && (
              <div className="waf-raw-row">
                <span className="waf-raw-label">Status</span>
                <code className="waf-raw-value">HTTP {rawSignals.statusCode}</code>
              </div>
            )}
            {hasSignificantHeaders && Object.entries(rawSignals.significantHeaders).slice(0, 8).map(([key, val]) => (
              <div key={key} className="waf-raw-row">
                <span className="waf-raw-label">{key}</span>
                <code className="waf-raw-value">{String(val).substring(0, 80)}</code>
              </div>
            ))}
            {rawSignals?.detectedCookies?.length > 0 && (
              <div className="waf-raw-row">
                <span className="waf-raw-label">WAF Cookies</span>
                <div className="waf-cookies-list">
                  {rawSignals.detectedCookies.map((c, i) => (
                    <code key={i} className="waf-cookie-chip">{c}</code>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}
